import { lazy, Suspense, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { isNative } from '@/platform/native';
import { FEEDBACK_ROLES } from './roles';
import { resetFeedbackState } from './state';
import { purgeWebFeedbackFor } from './outbox';

const Inner = lazy(() => import('./FeedbackHostInner'));

/**
 * Mounted once at the app root. Anonymous visitors, customer accounts, impersonated sessions and the console see and load nothing from it:
 * no flag request, no strings, no sheet code, no outbox. For a signed-in team member the inner host (flag, sheet, outbox) loads on its own.
 *
 * Whoever signs out, or is replaced by another person, leaves nothing behind for the next: the sheet and its draft are reset, and on the
 * website the queued notes of the person who left are removed from the browser (the app asks before sign-out, and keeps them).
 */
export function FeedbackHost() {
  const { user, impersonation } = useAuth();
  const userId = user?.id;
  const impersonating = !!impersonation;
  const previous = useRef<{ id: number; impersonating: boolean } | undefined>(undefined);

  useEffect(() => {
    const before = previous.current;
    if (before && before.id !== userId) {
      resetFeedbackState();
      // an impersonation ending is the platform admin leaving, not the customer: their notes are not touched
      if (!isNative() && !before.impersonating) void purgeWebFeedbackFor(before.id);
    }
    previous.current = userId === undefined ? undefined : { id: userId, impersonating };
  }, [userId, impersonating]);

  if (!user || impersonating || !FEEDBACK_ROLES.includes(user.role)) return null;
  return (
    <Suspense fallback={null}>
      <Inner />
    </Suspense>
  );
}
